import dayjs from 'dayjs';

export const timeToDayjs = (hhmm) => {
  if (!hhmm) return null;
  if (hhmm === '24:00') return dayjs('23:59', 'HH:mm');
  return dayjs(hhmm, 'HH:mm');
};

export const dayjsToTime = (val, isEnd) => {
  if (!val) return null;
  if (isEnd && val.format('HH:mm') === '23:59') return '24:00';
  return val.format('HH:mm');
};

export const timeToMinutes = (hhmm) => {
  if (!hhmm) return null;
  if (hhmm === '24:00') return 24 * 60;
  const [h, m] = String(hhmm).split(':').map(Number);
  return h * 60 + m;
};

export const minutesToTime = (mins) => {
  if (mins >= 24 * 60) return '24:00';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const daysInMonthFor = (year, month) =>
  dayjs(`${year}-${String(month).padStart(2, '0')}-01`).daysInMonth();

export const buildPeriodsFromTariff = (tariff) =>
  (tariff.periods || []).map((p) => ({
    dateFrom: p.dateFrom,
    dateTo: p.dateTo,
    slots: (p.slots || []).map((s) => ({
      timeRange: [timeToDayjs(s.startTime), timeToDayjs(s.endTime)],
      rate: s.rate,
    })),
  }));

export const buildDefaultPeriods = (year, month) => [
  {
    dateFrom: 1,
    dateTo: daysInMonthFor(year, month),
    slots: [
      {
        timeRange: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
        rate: undefined,
      },
    ],
  },
];

export const mapFormPeriods = (values) =>
  (values.periods || []).map((p) => ({
    dateFrom: p.dateFrom,
    dateTo: p.dateTo,
    slots: (p.slots || []).map((s) => ({
      startTime: dayjsToTime(s.timeRange?.[0], false),
      endTime: dayjsToTime(s.timeRange?.[1], true),
      rate: s.rate,
    })),
  }));

export const isBlank = (value) => value === undefined || value === null || value === '';

export const rangesOverlap = (aFrom, aTo, bFrom, bTo) => aFrom <= bTo && bFrom <= aTo;

export const parseDay = (value) => {
  if (isBlank(value)) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return NaN;
  return n;
};

export const getSlotBounds = (slot) => {
  const startTime = dayjsToTime(slot?.timeRange?.[0], false);
  const endTime = dayjsToTime(slot?.timeRange?.[1], true);
  if (!startTime || !endTime) return null;
  const start = timeToMinutes(startTime);
  const end = timeToMinutes(endTime);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return { start, end, startTime, endTime };
};

/** Live coverage analysis — separates incomplete fields from real coverage gaps. */
export const analyzeCoverage = (periods, daysInMonth, t) => {
  const incomplete = [];
  const gaps = [];
  const periodIssues = {};
  const periodHasIncomplete = {};

  const addPeriodIssue = (idx, msg, kind) => {
    if (!periodIssues[idx]) periodIssues[idx] = [];
    if (!periodIssues[idx].includes(msg)) periodIssues[idx].push(msg);
    if (kind === 'incomplete') {
      periodHasIncomplete[idx] = true;
      if (!incomplete.includes(msg)) incomplete.push(msg);
    } else if (!gaps.includes(msg)) {
      gaps.push(msg);
    }
  };

  const addGlobalGap = (msg) => {
    if (!gaps.includes(msg)) gaps.push(msg);
  };

  if (!periods || !periods.length) {
    return {
      isComplete: false,
      status: 'incomplete',
      issues: [t('validation.periodRequired')],
      incomplete: [t('validation.periodRequired')],
      gaps: [],
      periodIssues: {},
      periodHasIncomplete: {},
    };
  }

  const dayPeriods = [];

  for (let i = 0; i < periods.length; i++) {
    const p = periods[i];
    const dateFrom = Number(p.dateFrom);
    const dateTo = Number(p.dateTo);

    if (!p.dateFrom || !p.dateTo) {
      addPeriodIssue(i, t('validation.dateRequired'), 'incomplete');
      continue;
    }
    if (dateFrom < 1 || dateTo < 1) {
      addPeriodIssue(i, t('validation.dayMin'), 'gap');
      continue;
    }
    if (dateFrom > daysInMonth || dateTo > daysInMonth) {
      addPeriodIssue(i, t('validation.dayMax', { max: daysInMonth }), 'gap');
      continue;
    }
    if (dateFrom > dateTo) {
      addPeriodIssue(i, t('validation.dateOrder'), 'gap');
      continue;
    }
    if (!p.slots || !p.slots.length) {
      addPeriodIssue(i, t('validation.slotRequired'), 'incomplete');
      continue;
    }

    const slots = [];
    let slotsOk = true;
    for (let j = 0; j < p.slots.length; j++) {
      const s = p.slots[j];
      const startTime = dayjsToTime(s.timeRange?.[0], false);
      const endTime = dayjsToTime(s.timeRange?.[1], true);
      const hasTime = !!(startTime && endTime);
      const rateNum = Number(s.rate);
      const hasRate =
        !(s.rate === undefined || s.rate === null || s.rate === '') &&
        Number.isFinite(rateNum) &&
        rateNum >= 0;

      if (!hasTime) {
        addPeriodIssue(i, t('validation.emptySlot'), 'incomplete');
        slotsOk = false;
        continue;
      }
      if (!hasRate) {
        addPeriodIssue(i, t('validation.rateRequired'), 'incomplete');
        slotsOk = false;
        continue;
      }
      const start = timeToMinutes(startTime);
      const end = timeToMinutes(endTime);
      if (end <= start) {
        addPeriodIssue(i, t('validation.timeOrder'), 'gap');
        slotsOk = false;
        continue;
      }
      slots.push({ start, end, startTime, endTime });
    }

    if (!slotsOk || !slots.length) continue;

    slots.sort((a, b) => a.start - b.start);
    let cursor = 0;
    let slotCoverageOk = true;
    for (let j = 0; j < slots.length; j++) {
      const slot = slots[j];
      if (slot.start < cursor) {
        addPeriodIssue(
          i,
          t('validation.slotOverlap', { start: slot.startTime }),
          'gap',
        );
        slotCoverageOk = false;
      } else if (slot.start > cursor) {
        addPeriodIssue(
          i,
          t('validation.slotGap', {
            start: minutesToTime(cursor),
            end: slot.startTime,
          }),
          'gap',
        );
        slotCoverageOk = false;
      }
      cursor = Math.max(cursor, slot.end);
    }
    if (cursor < 24 * 60) {
      addPeriodIssue(
        i,
        t('validation.slotIncompleteDetail', {
          start: minutesToTime(cursor),
          end: '24:00',
        }),
        'gap',
      );
      slotCoverageOk = false;
    }

    if (slotCoverageOk) {
      dayPeriods.push({ dateFrom, dateTo, idx: i });
    }
  }

  if (incomplete.length === 0) {
    if (dayPeriods.length) {
      const sorted = [...dayPeriods].sort((a, b) => a.dateFrom - b.dateFrom);

      if (sorted[0].dateFrom > 1) {
        addGlobalGap(
          t('validation.dayGapDetail', {
            from: 1,
            to: sorted[0].dateFrom - 1,
          }),
        );
      }

      let dayCursor = 0;
      for (let i = 0; i < sorted.length; i++) {
        const p = sorted[i];
        if (dayCursor > 0 && p.dateFrom <= dayCursor) {
          addPeriodIssue(
            p.idx,
            t('validation.periodOverlapSimple', {
              from: p.dateFrom,
              to: Math.min(p.dateTo, dayCursor),
            }),
            'gap',
          );
        } else if (dayCursor > 0 && p.dateFrom > dayCursor + 1) {
          addGlobalGap(
            t('validation.dayGapDetail', {
              from: dayCursor + 1,
              to: p.dateFrom - 1,
            }),
          );
        }
        dayCursor = Math.max(dayCursor, p.dateTo);
      }

      if (dayCursor < daysInMonth) {
        addGlobalGap(
          t('validation.dayGapDetail', {
            from: dayCursor + 1,
            to: daysInMonth,
          }),
        );
      }
    }
  }

  const status =
    incomplete.length > 0 ? 'incomplete' : gaps.length > 0 ? 'gaps' : 'complete';

  return {
    isComplete: status === 'complete',
    status,
    issues: [...incomplete, ...gaps],
    incomplete,
    gaps,
    periodIssues,
    periodHasIncomplete,
  };
};

export const findNextTimeGap = (slots) => {
  const parsed = [];
  for (const s of slots || []) {
    const startTime = dayjsToTime(s?.timeRange?.[0], false);
    const endTime = dayjsToTime(s?.timeRange?.[1], true);
    if (!startTime || !endTime) continue;
    const start = timeToMinutes(startTime);
    const end = timeToMinutes(endTime);
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
      parsed.push({ start, end });
    }
  }
  parsed.sort((a, b) => a.start - b.start);

  const dayEnd = 24 * 60;
  let cursor = 0;
  for (const slot of parsed) {
    if (slot.start > cursor) {
      return { start: cursor, end: slot.start };
    }
    cursor = Math.max(cursor, slot.end);
  }
  if (cursor < dayEnd) {
    return { start: cursor, end: dayEnd };
  }
  return null;
};

export const findNextDayGap = (periods, daysInMonth) => {
  const ranges = [];
  for (const p of periods || []) {
    const from = Number(p?.dateFrom);
    const to = Number(p?.dateTo);
    if (
      Number.isFinite(from) &&
      Number.isFinite(to) &&
      from >= 1 &&
      to >= from &&
      from <= daysInMonth
    ) {
      ranges.push({ from, to: Math.min(to, daysInMonth) });
    }
  }
  ranges.sort((a, b) => a.from - b.from);

  let cursor = 0;
  for (const r of ranges) {
    if (r.from > cursor + 1) {
      return { dateFrom: cursor + 1, dateTo: r.from - 1 };
    }
    cursor = Math.max(cursor, r.to);
  }
  if (cursor < daysInMonth) {
    return { dateFrom: cursor + 1, dateTo: daysInMonth };
  }
  return null;
};

export const analyzeSlotsCoverage = (slots, t) => {
  if (!slots?.length) {
    return { ok: false, message: t('validation.slotRequiredList') };
  }

  const parsed = [];
  for (let i = 0; i < slots.length; i++) {
    const bounds = getSlotBounds(slots[i]);
    const rate = slots[i]?.rate;
    if (!bounds) {
      return { ok: false, message: t('validation.emptySlot') };
    }
    if (bounds.end <= bounds.start) {
      return { ok: false, message: t('validation.timeOrderSimple') };
    }
    if (isBlank(rate) || Number.isNaN(Number(rate)) || Number(rate) < 0) {
      return { ok: false, message: t('validation.rateRequired') };
    }
    parsed.push(bounds);
  }

  parsed.sort((a, b) => a.start - b.start);
  let cursor = 0;
  for (const slot of parsed) {
    if (slot.start < cursor) {
      return {
        ok: false,
        message: t('validation.slotOverlap', { start: slot.startTime }),
      };
    }
    if (slot.start > cursor) {
      return {
        ok: false,
        message: t('validation.slotGap', {
          start: minutesToTime(cursor),
          end: slot.startTime,
        }),
      };
    }
    cursor = Math.max(cursor, slot.end);
  }
  if (cursor < 24 * 60) {
    return {
      ok: false,
      message: t('validation.slotIncompleteDetail', {
        start: minutesToTime(cursor),
        end: '24:00',
      }),
    };
  }
  return { ok: true };
};

export const makeDayValidators = (form, periodIndex, daysInMonth, t) => {
  const validateDayValue = (value) => {
    if (isBlank(value)) {
      return Promise.reject(new Error(t('validation.dateRequired')));
    }
    const n = parseDay(value);
    if (!Number.isFinite(n) || !Number.isInteger(n)) {
      return Promise.reject(new Error(t('validation.dayInteger')));
    }
    if (n < 1 || n > daysInMonth) {
      return Promise.reject(new Error(t('validation.dayRange', { max: daysInMonth })));
    }
    return Promise.resolve(n);
  };

  const validateOrderAndOverlap = async (currentFrom, currentTo) => {
    if (currentFrom > currentTo) {
      return Promise.reject(new Error(t('validation.dateOrderSimple')));
    }

    const periods = form.getFieldValue('periods') || [];
    for (let i = 0; i < periods.length; i++) {
      if (i === periodIndex) continue;
      const otherFrom = parseDay(periods[i]?.dateFrom);
      const otherTo = parseDay(periods[i]?.dateTo);
      if (!Number.isFinite(otherFrom) || !Number.isFinite(otherTo)) continue;
      if (rangesOverlap(currentFrom, currentTo, otherFrom, otherTo)) {
        const from = Math.max(currentFrom, otherFrom);
        const to = Math.min(currentTo, otherTo);
        return Promise.reject(
          new Error(t('validation.periodOverlapSimple', { from, to })),
        );
      }
    }
    return Promise.resolve();
  };

  return {
    dateFrom: async (_, value) => {
      const from = await validateDayValue(value);
      const to = parseDay(form.getFieldValue(['periods', periodIndex, 'dateTo']));
      if (!Number.isFinite(to)) return Promise.resolve();
      return validateOrderAndOverlap(from, to);
    },
    dateTo: async (_, value) => {
      const to = await validateDayValue(value);
      const from = parseDay(form.getFieldValue(['periods', periodIndex, 'dateFrom']));
      if (!Number.isFinite(from)) return Promise.resolve();
      return validateOrderAndOverlap(from, to);
    },
  };
};

export const makeTimeRangeValidator = (form, periodIndex, slotIndex, t) => async (_, value) => {
  if (!value?.[0] || !value?.[1]) {
    return Promise.reject(new Error(t('validation.timeRequired')));
  }
  const startTime = dayjsToTime(value[0], false);
  const endTime = dayjsToTime(value[1], true);
  const start = timeToMinutes(startTime);
  const end = timeToMinutes(endTime);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return Promise.reject(new Error(t('validation.timeOrderSimple')));
  }

  const slots = form.getFieldValue(['periods', periodIndex, 'slots']) || [];
  for (let i = 0; i < slots.length; i++) {
    if (i === slotIndex) continue;
    const other = getSlotBounds(slots[i]);
    if (!other) continue;
    if (start < other.end && other.start < end) {
      return Promise.reject(
        new Error(t('validation.slotOverlap', { start: startTime })),
      );
    }
  }
  return Promise.resolve();
};

export const makeRateValidator = (t) => async (_, value) => {
  if (isBlank(value)) {
    return Promise.reject(new Error(t('validation.rateRequired')));
  }
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return Promise.reject(new Error(t('validation.rateNumber')));
  }
  if (n < 0) {
    return Promise.reject(new Error(t('validation.rateMin')));
  }
  return Promise.resolve();
};

export const makeSlotsListValidator = (t) => async (_, slots) => {
  // Field-level validators own empty time/rate messages — only report coverage geometry here.
  const hasIncompleteFields = (slots || []).some((slot) => {
    const bounds = getSlotBounds(slot);
    if (!bounds || bounds.end <= bounds.start) return true;
    const rate = slot?.rate;
    return isBlank(rate) || Number.isNaN(Number(rate)) || Number(rate) < 0;
  });
  if (hasIncompleteFields) {
    return Promise.resolve();
  }

  const result = analyzeSlotsCoverage(slots, t);
  if (!result.ok) {
    return Promise.reject(new Error(result.message));
  }
  return Promise.resolve();
};

export const makePeriodsListValidator = (daysInMonth, t) => async (_, periods) => {
  if (!periods?.length) {
    return Promise.reject(new Error(t('validation.periodRequired')));
  }

  const ready = [];
  for (const p of periods) {
    const from = parseDay(p?.dateFrom);
    const to = parseDay(p?.dateTo);
    if (!Number.isFinite(from) || !Number.isFinite(to) || from > to) {
      // Day field validators own these messages while the user is still filling the form.
      return Promise.resolve();
    }

    const slotsIncomplete = (p?.slots || []).some((slot) => {
      const bounds = getSlotBounds(slot);
      if (!bounds || bounds.end <= bounds.start) return true;
      const rate = slot?.rate;
      return isBlank(rate) || Number.isNaN(Number(rate)) || Number(rate) < 0;
    });
    if (slotsIncomplete) {
      // Slot/time/rate field validators own incomplete-field messages.
      return Promise.resolve();
    }

    const slotsResult = analyzeSlotsCoverage(p?.slots, t);
    if (!slotsResult.ok) {
      return Promise.reject(new Error(slotsResult.message));
    }
    ready.push({ from, to });
  }

  if (!ready.length) {
    return Promise.resolve();
  }

  ready.sort((a, b) => a.from - b.from);
  if (ready[0].from !== 1) {
    return Promise.reject(
      new Error(t('validation.dayGapDetail', { from: 1, to: ready[0].from - 1 })),
    );
  }

  let cursor = 0;
  for (const p of ready) {
    if (p.from <= cursor) {
      return Promise.reject(
        new Error(
          t('validation.periodOverlapSimple', {
            from: p.from,
            to: Math.min(p.to, cursor),
          }),
        ),
      );
    }
    if (cursor > 0 && p.from > cursor + 1) {
      return Promise.reject(
        new Error(t('validation.dayGapDetail', { from: cursor + 1, to: p.from - 1 })),
      );
    }
    cursor = Math.max(cursor, p.to);
  }

  if (cursor < daysInMonth) {
    return Promise.reject(
      new Error(t('validation.dayGapDetail', { from: cursor + 1, to: daysInMonth })),
    );
  }
  return Promise.resolve();
};
